import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { onLCP, onINP, onCLS } from 'web-vitals';
import App from './App';
import { initSentry, captureException } from './lib/sentry';
import { emit } from '@/lib/events';
import './i18n/config';
import './index.css';

initSentry();

onLCP((m) => emit('web_vital', { name: 'LCP', value: Math.round(m.value) }));
onINP((m) => emit('web_vital', { name: 'INP', value: Math.round(m.value) }));
onCLS((m) => emit('web_vital', { name: 'CLS', value: Number(m.value.toFixed(3)) }));

// PWA shell — register the service worker once, after the page is loaded
// so we don't compete with first-paint work. Skip in dev (Vite serves
// modules unbundled, SW would cache stale ESM transforms).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
      console.warn('SW registration failed', err);
    });
  });
}

// F1.10 hardened defaults. Without these every useQuery() was: retry=3 (OK),
// staleTime=0 (refetch on every mount / window-focus → wasteful + flicker),
// no error reporting (query failures invisible to Sentry).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx — client errors aren't going to fix themselves.
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      staleTime: 5 * 60 * 1000, // 5 min — most static data
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Soft-fail queries (no throwOnError) still need to show up in Sentry
      // so we can see broken data sources in prod. Tag with query key.
      captureException(error, {
        tags: { source: 'tanstack-query' },
        contexts: { query: { key: JSON.stringify(query.queryKey) } },
      });
    },
  }),
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
